import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1 rounded-full",
    "px-2 py-0.5 text-xs font-medium",
    "transition-colors duration-[120ms]",
  ].join(" "),
  {
    variants: {
      variant: {
        /** Receita/ouro: para valores monetarios e status de pagamento */
        gold: [
          "bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))]",
          "border border-[hsl(var(--gold)/0.25)]",
        ].join(" "),

        /** Lucro/crescimento: deltas positivos, +% */
        profit: [
          "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]",
          "border border-[hsl(var(--primary)/0.25)]",
        ].join(" "),

        /** Queda: deltas negativos */
        loss: [
          "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]",
          "border border-[hsl(var(--destructive)/0.25)]",
        ].join(" "),

        /** Default/verde: ativo, status positivo */
        default: [
          "bg-primary/15 text-primary",
          "border border-primary/25",
        ].join(" "),

        /** Success: alias de profit para compatibilidade */
        success: [
          "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]",
          "border border-[hsl(var(--primary)/0.25)]",
        ].join(" "),

        /** Warning: ouro para avisos nao-financeiros */
        warning: [
          "bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))]",
          "border border-[hsl(var(--gold)/0.25)]",
        ].join(" "),

        /** Destructive: vermelho queda */
        destructive: [
          "bg-destructive/12 text-destructive",
          "border border-destructive/25",
        ].join(" "),

        /** Muted: neutro, status inativo */
        muted: [
          "bg-[hsl(var(--surface-raised))] text-muted-foreground",
          "border border-[hsl(var(--border))]",
        ].join(" "),

        /** Outline: transparente com borda */
        outline: [
          "border border-[hsl(var(--border-strong))] text-foreground bg-transparent",
        ].join(" "),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps): React.JSX.Element {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
